import React, { useState, useCallback, useContext, useEffect } from 'react';
import JumboContentLayout from '@jumbo/components/JumboContentLayout';
import PageHeader from '../../../layouts/shared/headers/PageHeader/PageHeader';
import useJumboTheme from '@jumbo/hooks/useJumboTheme';
import {
    Box,
    Button,
    CircularProgress,
    Pagination,
    Paper,
    Stack,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { ApiService } from 'app/servicesTwo/ApiService';
import MenorItem from './MenorItem';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { PermissionContext } from 'app/contexts/PermissionContext';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';
import useTokenCheck from '@jumbo/hooks/useTokenCheck';
import JumboSearchWithoutDebounce from '@jumbo/components/JumboSearchWithoutDebounce';

const MenorList = () => {
    useTokenCheck();
    const { theme } = useJumboTheme();
    const location = useLocation();
    const Swal = useSwalWrapper();
    const { hasPermission } = useContext(PermissionContext);

    const lg = useMediaQuery(theme.breakpoints.down('lg'));
    const layoutOptions = React.useMemo(
        () => ({
            sidebar: {
                sx: {
                    [theme.breakpoints.up('lg')]: {
                        position: 'sticky',
                        zIndex: 5,
                        top: 96,
                        minHeight: 'auto',
                    },
                    [theme.breakpoints.down('lg')]: { display: 'none' },
                },
            },
            wrapper: { sx: { alignItems: 'flex-start' } },
        }),
        [theme]
    );

    const PER_PAGE = 10;

    const [searchParams, setSearchParams] = useSearchParams();
    const currentSearch = searchParams.get('search') || '';
    const currentPage = Number(searchParams.get('page')) || 1;

    const [menores, setMenores] = useState(null);
    const [count, setCount] = useState(0);
    const [searchTerm, setSearchTerm] = useState(currentSearch);
    const [loading, setLoading] = useState(false);

    const fetchMenores = useCallback(async (search, page) => {
        setLoading(true);
        try {
            const response = await ApiService.get('/menores', {
                params: { search, page, per_page: PER_PAGE },
            });
            setMenores(response.data?.data || []);
            setCount(response.data?.total_pages || 0);
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Busca sempre que search/page da URL mudam (inclui carga inicial = lista todos)
    useEffect(() => {
        fetchMenores(currentSearch, currentPage);
    }, [currentSearch, currentPage, fetchMenores]);

    // Mantém o input em sincronia ao voltar da edição (URL com search)
    useEffect(() => {
        setSearchTerm(currentSearch);
    }, [currentSearch]);

    const handleSearch = () => {
        const term = (searchTerm || '').trim();
        setSearchParams(term ? { search: term, page: '1' } : { page: '1' });
    };

    const handlePageChange = (event, value) => {
        const term = (currentSearch || '').trim();
        setSearchParams(term ? { search: term, page: String(value) } : { page: String(value) });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    useEffect(() => {
        Swal.close();
    }, [location.pathname, Swal]);

    return (
        <JumboContentLayout
            header={<PageHeader title='Menores' />}
            layoutOptions={layoutOptions}
        >
            {lg && <Stack spacing={2} direction='row' sx={{ mb: 3, mt: -2 }} />}
            <Paper sx={{ p: '40px' }}>
                <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>

                    <Link
                        to='/app/novo-menor'
                        style={{
                            textDecoration: 'none',
                            pointerEvents: !hasPermission('Pessoas', 'criar') ? 'none' : '',
                        }}
                        state={{ backUrl: location.pathname + location.search }}
                    >
                        <LoadingButton
                            color='success'
                            variant='contained'
                            size='large'
                            disabled={!hasPermission('Pessoas', 'criar')}
                            sx={{ maxWidth: { md: '200px' }, marginBottom: '2rem' }}
                        >
                            Novo Cadastro
                        </LoadingButton>
                    </Link>
                </Box>

                <Typography
                    variant='h6'
                    sx={{ fontWeight: '500', marginBottom: '1rem', marginLeft: '1rem' }}
                >
                    Busque por nome ou CPF do menor
                </Typography>

                <Box
                    display='flex'
                    alignItems='center'
                    gap='1rem'
                    marginBottom='1rem'
                    flexWrap='wrap'
                    onKeyDown={handleKeyDown}
                >
                    <JumboSearchWithoutDebounce
                        defaultValue={searchTerm}
                        onChange={(e) => setSearchTerm(e)}
                        placeholder='Digite nome ou CPF do menor...'
                        sx={{ width: '440px' }}
                    />
                    <Button variant='contained' onClick={handleSearch}>
                        Filtrar
                    </Button>
                </Box>

                <>
                    {loading && (
                        <Box display='flex' justifyContent='center'>
                            <CircularProgress />
                        </Box>
                    )}
                    {menores != null && menores.length === 0 && !loading && (
                        <Typography>Sem resultados correspondentes</Typography>
                    )}
                    {menores &&
                        menores.map((menor) => (
                            <MenorItem
                                key={menor.menor_id}
                                item={menor}
                                setMenores={(data) => setMenores([...data])}
                                menores={menores}
                            />
                        ))}

                    {!loading && count > 1 && (
                        <Box display='flex' justifyContent='start' mt={3}>
                            <Pagination
                                count={count}
                                page={currentPage}
                                onChange={handlePageChange}
                                color='primary'
                            />
                        </Box>
                    )}
                </>
            </Paper>
        </JumboContentLayout>
    );
};

export default MenorList;
